<template>
	<Teleport to="body">
		<div class="hdl-emoji-picker dropdown" ref="dropdown" :style="floatingStyles" v-show="dropdownOpen" v-click-away="clickedAway">
			<div class="search">
				<hdl-textbox
					:model-value="terms"
					@update:modelValue="termsInput"
					:icon="['far', 'magnifying-glass']"
					hdl-style="default"
					placeholder="Search"
					ref="search"
					inputId="emojiSearch"
				/>
			</div>
			<div class="categories">
				<div
					v-for="category of emojiCategoryChoices"
					:key="category.value"
					class="emoji-category"
					@click.prevent="categorySelected(category.value)"
					:class="{'selected': selectedCategory === category.value}"
				>
					<font-awesome-icon :icon="category.icon" fixed-width />
				</div>
			</div>
			<div class="emojis hdl-scroll">
				<div
					v-for="emoji of showingEmojis"
					:key="emoji.emoji"
					class="emoji hover:bg-fill-hover cursor-pointer rounded p-1"
					:class="{'selected': isSelected(emoji.emoji)}"
					@click.prevent="select(emoji.emoji)"
				>
					<emoji
						:code-point="emoji.emoji"
						size="lg"
					/>
				</div>
			</div>
			<div
				class="text-disabled select-none text-center italic"
				v-if="showingEmojis && showingEmojis.length === 0"
			>
				No results
			</div>
			<div class="recents">
				<div
					v-for="emoji of recents"
					:key="emoji"
					class="emoji hover:bg-fill-hover cursor-pointer rounded p-1"
					:class="{'selected': isSelected(emoji)}"
					@click.prevent="select(emoji)"
				>
					<emoji
						:code-point="emoji"
						size="lg"
					/>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-facing-decorator';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
	faFaceSmile, faFlag, faPizzaSlice, faBox, faDog, faSymbols, faHouse,
	faMagnifyingGlass, faFootball, faPlaneDeparture, faPerson
} from '@fortawesome/pro-regular-svg-icons';

library.add(
	faFaceSmile, faFlag, faPizzaSlice, faBox, faDog, faSymbols, faHouse,
	faMagnifyingGlass, faFootball, faPlaneDeparture, faPerson
);

import { computePosition, autoUpdate, Placement, autoPlacement, flip } from '@floating-ui/dom';
import { Choice } from '@/common/types';
import { fetcher, FetchResult } from '@/common/fetcher';
import debounce from 'lodash.debounce';
import HdlTextbox from '../HdlTextbox.vue';

import EmojiGroupsRaw from '@/common/emoji/data-by-group.json';
import emojiHandler, { EmojiDialogCallbacks } from './emojiHandler';
const EmojiGroups = EmojiGroupsRaw as Readonly<EmojiGroupsType>;

export declare type EmojiCategory =
	'default' | 'smileys_emotion' | 'food_drink' | 'people_body' | 'objects' | 'animals_nature' | 'travel_places' | 'symbols' | 'activities' | 'flags';


declare type EmojiInfo = {
	emoji: string;
	skin_tone_support: boolean;
	name: string;
	slug: string;
	unicode_version: string;
	emoji_version: string;
};

declare type EmojiGroup = {
	name: string;
	slug: string;
	emojis: Array<EmojiInfo>;
};

declare type EmojiGroupsType = Array<EmojiGroup>;

const EmojiCategoryChoices: Array<Choice<EmojiCategory>> = [
	/*{
		label: 'Default',
		value: 'default',
		icon: ['far', 'house']
	},*/ {
		label: 'Smileys & Emotions',
		value: 'smileys_emotion',
		icon: ['far', 'face-smile']
	}, {
		label: 'People & Body',
		value: 'people_body',
		icon: ['far', 'person']
	}, {
		label: 'Animals & Nature',
		value: 'animals_nature',
		icon: ['far', 'dog']
	}, {
		label: 'Food & Drink',
		value: 'food_drink',
		icon: ['far', 'pizza-slice']
	}, {
		label: 'Travel & Places',
		value: 'travel_places',
		icon: ['far', 'plane-departure']
	}, {
		label: 'Activities',
		value: 'activities',
		icon: ['far', 'football']
	}, {
		label: 'Objects',
		value: 'objects',
		icon: ['far', 'box']
	}, {
		label: 'Symbols',
		value: 'symbols',
		icon: ['far', 'symbols']
	}, {
		label: 'Flags',
		value: 'flags',
		icon: ['far', 'flag']
	}
];

@Component
export default class EmojiPickerDialog extends Vue {
	modelValue: string = '';

	callbacks: EmojiDialogCallbacks | null = null;

	emojiCategoryChoices: Array<Choice<EmojiCategory>> = EmojiCategoryChoices;

	dropdownOpen: boolean = false;

	floatingStyles: {[key: string]: string} = {};

	terms: string = '';

	recents: Array<string> | null = null;

	showingEmojis: Array<EmojiInfo> | null = null;

	selectedCategory: EmojiCategory = 'smileys_emotion';

	buttonEl: HTMLElement | null = null;

	floatingUICleanup: (() => void) | null = null;

	debouncedPerformSearch: (() => void) | null = null;

	updateDropdown: (() => void) | null = null;

	created(): void {
		this.debouncedPerformSearch = debounce(this.performSearch, 200);
		this.fetchDefaultPageAndRecents();
	}

	mounted(): void {
		//@ts-ignore
		emojiHandler.registerDialog(this);
	}

	unmounted(): void {
		//@ts-ignore
		emojiHandler.unregisterDialog(this);
		this.close();
	}

	fetchDefaultPageAndRecents(): void {
		this.selectByCurrentCategory();

		fetcher.Get(
			'/emoji/?recents=1'
		).then(
			(result: FetchResult) => {
				if (result.response.ok && result.json.success) {
					//this.showingEmojis = result.json.data.emojis;
					this.recents = result.json.data.recents;
				}
			}
		);
	}

	selectByCurrentCategory(): void {
		let emojis = EmojiGroups.filter((eg: EmojiGroup) => eg.slug === this.selectedCategory);

		// TODO handle default properly
		if (this.selectedCategory === 'default') {
			emojis = [EmojiGroups[0]];
		}

		if (emojis.length === 0) {
			return;
		}

		this.showingEmojis = emojis[0].emojis;
	}

	performSearch(): void {
		//this.fetchEmojis();
		if (this.terms.trim().length === 0) {
			this.selectByCurrentCategory();
			return;
		}

		const emojis = [];
		const uniqueTerms = this.terms.split(" ").map(t => t.toLowerCase());

		for (const group of EmojiGroups) {
			for (const emoji of group.emojis) {
				if (uniqueTerms.some((t) => emoji.name.includes(t) || t === emoji.name)) {
					emojis.push(emoji);
				}
			}
		}

		this.showingEmojis = emojis;
	}

	termsInput(newTerms: string): void {
		this.terms = newTerms;

		this.debouncedPerformSearch?.();
	}

	select(emoji: string): void {
		this.modelValue = emoji;

		if (this.callbacks && this.callbacks.valueUpdated) {
			this.callbacks.valueUpdated(emoji);
		}

		fetcher.Put(`/emoji/?code=${encodeURIComponent(emoji)}`);

		if (this.recents) {
			const inRecentsIndex = this.recents.indexOf(emoji);

			if (inRecentsIndex > -1) {
				this.recents.splice(inRecentsIndex, 1);
				this.recents.unshift(emoji);
			} else {
				this.recents.unshift(emoji);
				this.recents.splice(9, 1);
			}
		} else {
			this.recents = [emoji];
		}

		this.close();
	}

	isSelected(emoji: string): boolean {
		return this.modelValue === emoji;
	}

	categorySelected(category: EmojiCategory): void {
		this.selectedCategory = category;
		//this.fetchEmojis();
		this.selectByCurrentCategory();
	}

	setupFloatingUI(handler: HTMLElement): void {
		if (this.floatingUICleanup !== null) {
			this.floatingUICleanup();
			this.floatingUICleanup = null;
		}

		const dropdown = this.$refs.dropdown as HTMLDivElement | undefined;

		if (dropdown === undefined) return;

		this.floatingUICleanup = autoUpdate(handler, dropdown, () => {
			computePosition(handler, dropdown, {
				placement: 'bottom-start',
				strategy: 'absolute',
				middleware: [flip()]
			}).then(({x, y}) => {
				Object.assign(dropdown.style, {
					left: `${x}px`,
					top: `${y}px`
				});
			});
		});
	}

	bindTo(buttonEl: HTMLElement, initialValue: string, callbacks: EmojiDialogCallbacks): void {
		this.buttonEl = buttonEl;
		this.modelValue = initialValue;
		this.callbacks = callbacks;
		this.setupFloatingUI(buttonEl);
	}

	unbind(): void {
		if (this.floatingUICleanup) {
			this.floatingUICleanup();
		}
		this.buttonEl = null;
		this.modelValue = '';
		this.callbacks = null;
	}

	get isBound(): boolean {
		return this.callbacks !== null;
	}

	toggle(): void {
		if (this.dropdownOpen) {
			this.close();
		} else {
			this.open();
		}
	}

	open(): void {
		this.terms = '';
		this.selectedCategory = 'default';
		this.dropdownOpen = true;

		if (this.callbacks && this.callbacks.opened) {
			this.callbacks.opened();
		}

		this.$nextTick(() => {
			const search = this.$refs.search as HdlTextbox;
			search.focus();
		});

		window.addEventListener('mousedown', this.clickedAway);
	}

	close(): void {
		this.dropdownOpen = false;
		window.removeEventListener('mousedown', this.clickedAway);
		if (this.callbacks && this.callbacks.closed) {
			this.callbacks.closed();
		}
	}

	clickedAway(event: MouseEvent): void {
		if (!this.dropdownOpen) {
			return;
		}

		if (
			(this.buttonEl && this.buttonEl.contains(event.target as HTMLElement))
			|| (this.$refs.dropdown as HTMLElement).contains(event.target as HTMLElement)
		) {
			return;
		}

		this.close();
	}
}
</script>

<style lang="scss">
.hdl-emoji-picker {
	&.dropdown {
		@apply absolute bg-fill border border-border-gray shadow-md rounded;

		z-index: 100;

		> .search {
			@apply px-2 py-2;
		}

		> .categories {
			@apply px-2 pt-1 pb-2;
			display: grid;
			column-gap: .2rem;
			grid-template-columns: repeat(9, 1fr);

			.emoji-category {
				@apply cursor-pointer hover:bg-fill-hover rounded p-1 py-2 flex flex-row justify-center;

				&.selected {
					@apply bg-fill-gray;
				}
			}
		}

		> .recents,
		> .emojis {
			@apply px-2 border-t border-border-gray;
			overflow-y: auto;
			overflow-x: hidden;
			max-height: 223px;

			display: grid;
			column-gap: .1rem;
			row-gap: .1rem;
			grid-template-columns: repeat(9, 1fr);

			.emoji {
				&.selected {
					@apply bg-fill-gray;
				}

				> img {
					width: 24px;
					min-width: 24px;
					max-width: 24px;
					height: 24px;
					min-height: 24px;
					max-height: 24px;
				}
			}
		}

		> .emojis {
			@apply py-3;
		}

		> .recents {
			@apply py-2;
		}
	}
}
</style>
